import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Button } from '../../../components/Button';
import { TextInput } from '../../../components/TextInput';
import type { AdminTourAnchor } from '../../../types/adminTours';

/** The "no anchor" choice. It is a mode rather than an anchor, so it has no id of its own. */
const CENTERED_LABEL = 'Centered card (no highlight)';
const CENTERED_HINT = 'The step shows in the middle of the page and nothing is outlined.';

export type AnchorLoadStatus = 'idle' | 'loading' | 'ready' | 'failed';

export interface AnchorPickerProps {
  /** Every element a step on this platform can point at. */
  anchors: AdminTourAnchor[];
  status: AnchorLoadStatus;
  /** Names the picker, e.g. "Where should this step point?". */
  heading: string;
  onRetry: () => void;
  /** Null is the centered card. */
  onSelect: (anchorId: string | null) => void;
  onCancel: () => void;
}

const matches = (anchor: AdminTourAnchor, term: string): boolean =>
  anchor.label.toLowerCase().includes(term) ||
  anchor.id.toLowerCase().includes(term) ||
  anchor.route.toLowerCase().includes(term);

/**
 * The anchor list, as a searchable listbox.
 *
 * Search plus `aria-activedescendant` rather than one tab stop per anchor: the manifest already
 * carries dozens of anchors, and a keyboard user should not have to Tab through all of them to
 * reach the Cancel button. The centered-card choice sits above the filter rather than inside it,
 * because it is not an anchor and stays available when a search matches nothing.
 */
export const AnchorPicker = ({
  anchors,
  status,
  heading,
  onRetry,
  onSelect,
  onCancel,
}: AnchorPickerProps) => {
  const idPrefix = useId();
  const listId = `${idPrefix}-list`;
  const headingId = `${idPrefix}-heading`;
  const [term, setTerm] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Focused on open rather than with `autoFocus`, which moves focus on every re-render the browser
  // decides to treat as a mount and is flagged by the accessibility lint.
  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  const filtered = useMemo(() => {
    const needle = term.trim().toLowerCase();
    return needle ? anchors.filter((anchor) => matches(anchor, needle)) : anchors;
  }, [anchors, term]);

  // The centered card is always index 0, so an empty search result still has something to select.
  const options: (AdminTourAnchor | null)[] = useMemo(() => [null, ...filtered], [filtered]);
  const active = Math.min(activeIndex, options.length - 1);
  const optionId = (index: number) => `${idPrefix}-option-${index}`;

  const scrollTo = (index: number) => {
    // Guarded, like `useAnchorState` does: jsdom has no scrollIntoView, and keyboard navigation is
    // not worth an exception in a test environment.
    const row = listRef.current?.children[index];
    if (row instanceof HTMLElement && typeof row.scrollIntoView === 'function') {
      row.scrollIntoView({ block: 'nearest' });
    }
  };

  const moveTo = (index: number) => {
    const next = (index + options.length) % options.length;
    setActiveIndex(next);
    scrollTo(next);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveTo(active + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveTo(active - 1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      moveTo(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      moveTo(options.length - 1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const option = options[active];
      if (option !== undefined) onSelect(option === null ? null : option.id);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onCancel();
    }
  };

  return (
    <div className="anchor-picker" data-testid="anchor-picker">
      <h3 className="anchor-picker__heading" id={headingId}>
        {heading}
      </h3>

      <TextInput
        ref={searchRef}
        className="anchor-picker__search"
        label="Search elements"
        type="search"
        role="combobox"
        autoComplete="off"
        aria-expanded
        aria-autocomplete="list"
        aria-controls={listId}
        aria-activedescendant={optionId(active)}
        value={term}
        onChange={(event) => {
          setTerm(event.target.value);
          setActiveIndex(0);
        }}
        onKeyDown={handleKeyDown}
        data-testid="anchor-picker-search"
      />

      {status === 'loading' && <p className="anchor-picker__note">Loading the element list...</p>}

      {status === 'failed' && (
        <div className="anchor-picker__note anchor-picker__note--error" role="alert">
          <p className="anchor-picker__note-text">
            The element list could not be loaded. You can still add a centered step.
          </p>
          <Button
            type="button"
            variant="secondary"
            onClick={onRetry}
            data-testid="anchor-picker-retry"
          >
            Try again
          </Button>
        </div>
      )}

      {/* The name belongs on the listbox itself. On the wrapper it would be inert: a div with no
          role has nothing for `aria-labelledby` to name. */}
      <ul
        className="anchor-picker__list"
        id={listId}
        role="listbox"
        aria-labelledby={headingId}
        ref={listRef}
      >
        {options.map((option, index) => {
          const selected = index === active;
          return (
            <li
              key={option === null ? 'centered' : option.id}
              id={optionId(index)}
              role="option"
              aria-selected={selected}
              className={`anchor-picker__option${selected ? ' anchor-picker__option--active' : ''}`}
              /*
                Selected on click, not on pointer down. Click is what an assistive technology
                dispatches when it activates an option, and it is primary-button only - pointer
                down would commit a choice on a right-click. Pointer down only blocks the default,
                so the search field keeps focus rather than losing it to a row that has no keyboard
                behaviour of its own; the listbox drives that through aria-activedescendant, which
                is why these rows are reachable programmatically (tabIndex -1) but not by Tab.

                The key handler is for the AT that moves real focus onto an option rather than
                driving the combobox: everyone else arrives here through the search field's Enter.
              */
              tabIndex={-1}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onSelect(option === null ? null : option.id)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                onSelect(option === null ? null : option.id);
              }}
              data-testid={`anchor-option-${option === null ? 'centered' : option.id}`}
            >
              <span className="anchor-picker__option-label">
                {option === null ? CENTERED_LABEL : option.label}
              </span>
              <span className="anchor-picker__option-route">
                {option === null ? CENTERED_HINT : option.route}
              </span>
            </li>
          );
        })}
      </ul>

      {status === 'ready' && filtered.length === 0 && (
        <p className="anchor-picker__note" data-testid="anchor-picker-empty">
          No element matches that. Clear the search to see them all.
        </p>
      )}

      <div className="anchor-picker__actions">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          data-testid="anchor-picker-cancel"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};
